import { addressLine } from '../../lib/format.js'
import { humanise } from '../ui.js'
import { Label, Panel, RecordHeader, Tag } from './panes.js'
import type { Job } from './types.js'

/**
 * The client and the site, as the two cards Work opens on.
 *
 * Both are summaries with a way through to the whole record. The site sits
 * above the client — the property is what the van is going to — and the link
 * switches to the Client tab rather than leaving the job. That tab draws both
 * again in its side rail (`ClientRail`), where the client links to the client
 * screen proper.
 */

export function ClientCard({ job, onOpen }: { job: Job; onOpen: () => void }) {
  const client = job.client

  return (
    <Panel>
      <RecordHeader
        icon="clients"
        kind="Client"
        name={client.name}
        detail={
          client.billingAddress ? `Bills to ${addressLine(client.billingAddress)}` : undefined
        }
        action={
          <button
            type="button"
            onClick={onOpen}
            className="text-xs font-semibold text-brand-600 hover:text-brand-700"
          >
            Open
          </button>
        }
      />

      {/* Indented to the name, so the facts read as the client's. Terms are a
          tag, and a tax number is one only when there is one. */}
      <div className="flex flex-wrap gap-1.5 pl-[42px]">
        <Tag>{humanise(client.type)}</Tag>
        {client.paymentTermsDays === 0 ? (
          <Tag>Pays on the day</Tag>
        ) : (
          <Tag>{`${client.paymentTermsDays} day terms`}</Tag>
        )}
        {/* The one tag that changes what somebody does next, so it is the only
            coloured one. `createdAt` is deliberately not drawn as "client
            since": on 20,296 imported clients it is the date of the migration
            run, not the date the business took them on. */}
        {client.isOnCreditHold ? <Tag tone="red">On credit hold</Tag> : null}
        {client.taxNumber ? <Tag>Tax no. {client.taxNumber}</Tag> : null}
      </div>
    </Panel>
  )
}

export function SiteCard({
  job,
  onOpen,
}: {
  job: Job
  onOpen?: () => void
}) {
  const site = job.site

  if (!site) {
    return (
      <Panel>
        <div className="flex items-center gap-2.5">
          <Label>Site</Label>
          {/* The picker itself is on the Client tab and only there. A second
              copy on Work would be two live pickers on one page. */}
          {onOpen ? (
            <button
              type="button"
              onClick={onOpen}
              className="ml-auto text-xs font-semibold text-brand-600 hover:text-brand-700"
            >
              Set the address
            </button>
          ) : null}
        </div>
        <p className="text-[13px] text-ink-500">
          This job names no property. ServiceM8 kept the address on the job itself, so an imported
          job with a one-off address has nothing to link to.
        </p>
      </Panel>
    )
  }

  const line = addressLine(site.address)

  return (
    <Panel>
      <RecordHeader
        icon="map"
        kind="Site"
        name={site.name ?? line}
        detail={site.name ? line : undefined}
        action={
          onOpen ? (
            <button
              type="button"
              onClick={onOpen}
              className="text-xs font-semibold text-brand-600 hover:text-brand-700"
            >
              Open
            </button>
          ) : undefined
        }
      />

      {site.accessNotes ? (
        <p className="pl-[42px] text-[13px] leading-relaxed whitespace-pre-wrap text-ink-700">
          {site.accessNotes}
        </p>
      ) : null}

      {site.hazardNotes || site.hasPets || site.accessMethod || site.gateCode ? (
        <div className="flex flex-wrap gap-1.5 pl-[42px]">
          {site.hazardNotes ? <Tag tone="red">Hazard on site</Tag> : null}
          {site.hasPets ? <Tag tone="dark">Pets on site</Tag> : null}
          {site.accessMethod ? <Tag>{humanise(site.accessMethod)}</Tag> : null}
          {site.gateCode ? (
            <Tag>
              Code <span className="font-mono font-semibold">{site.gateCode}</span>
            </Tag>
          ) : null}
        </div>
      ) : null}
    </Panel>
  )
}
